import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import { Box, Button, Chip, CircularProgress, Collapse, InputAdornment, Skeleton, TextField, Typography } from "@mui/material";
import { WidgetComponentMaps } from "../common/WidgetComponentMaps";
import { colors } from "../ui/colors";
import { brandButton, filledButton, outLinedButton } from "../ui/styles/buttonStyles";
import { useInvoiceNumbering } from "./hooks/useInvoiceNumbering";
import { INVOICE_NUMBERING_MODES } from "./types/invoiceNumbering.types";

export const InvoiceNumberingWidget = ({ swapyName = "invoiceNumberingWidget" }: { swapyName?: string }) => {
  const { value, saved, isHydrated, isDirty, isStartNumberValid, isSaving, setMode, setStartNumber, reset, save } = useInvoiceNumbering();

  const isSequentialSelected = value.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL;
  const isCurrentlyTimestamped = saved.invoiceNumberingMode === INVOICE_NUMBERING_MODES.TIMESTAMPED;
  const isSequentialSaved = saved.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL;

  return (
    <WidgetComponentMaps
      swapyName={swapyName}
      title="Invoice numbering"
      description="Choose how job and deposit invoices are numbered for this organization."
      icon={<ReceiptLongOutlinedIcon />}
      variant={isSequentialSaved ? "warning" : "default"}
      sxProp={{
        minHeight: "auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, px: 0.5, pt: 1, pb: 0.5, width: "100%" }}>
        {!isHydrated ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Skeleton variant="rounded" width="40%" height={26} sx={{ borderRadius: "12px" }} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: "14px" }} />
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: "14px" }} />
            </Box>
            <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Active Status Badge & Dirty Indicator */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <Chip
                size="small"
                icon={
                  isSequentialSaved ? (
                    <FormatListNumberedOutlinedIcon style={{ fontSize: 14 }} />
                  ) : (
                    <AccessTimeOutlinedIcon style={{ fontSize: 14 }} />
                  )
                }
                label={isSequentialSaved ? `Current: Sequential (#${saved.invoiceStartNumber})` : "Current: Timestamped (Legacy)"}
                sx={{
                  height: 28,
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: "999px",
                  bgcolor: isSequentialSaved ? "rgba(251, 142, 0, 0.1)" : "rgba(100, 116, 139, 0.08)",
                  color: isSequentialSaved ? colors.orange.base : "var(--fx-surface-textsecondary, #64748B)",
                  border: isSequentialSaved ? `1px solid ${colors.orange[200]}` : "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                  px: 0.5,
                }}
              />

              {isDirty && (
                <Chip
                  size="small"
                  label="Unsaved Changes"
                  icon={
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        ml: 1,
                        mr: -0.5,
                        bgcolor: "#f59e0b",
                        boxShadow: "0 0 0 3px rgba(245,158,11,0.25)",
                      }}
                    />
                  }
                  sx={{
                    height: 26,
                    fontWeight: 700,
                    fontSize: 11,
                    borderRadius: "999px",
                    bgcolor: "rgba(245, 158, 11, 0.12)",
                    color: "#b45309",
                    border: "1px solid rgba(245, 158, 11, 0.3)",
                    px: 0.5,
                  }}
                />
              )}
            </Box>

            {/* Interactive Mode Selection Cards */}
            <Box
              data-swapy-no-drag
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {/* Option 1: Timestamped (Legacy) */}
              <Box
                onClick={() => setMode(INVOICE_NUMBERING_MODES.TIMESTAMPED)}
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  userSelect: "none",
                  border: !isSequentialSelected
                    ? `2px solid ${colors.orange.base}`
                    : `1px solid ${colors.gray[200] || "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.1)"}`,
                  bgcolor: !isSequentialSelected
                    ? `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`
                    : "var(--fx-surface-base, #FFFFFF)",
                  boxShadow: !isSequentialSelected ? "0 4px 14px rgba(251, 142, 0, 0.12)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 1.25,
                  position: "relative",
                  "&:hover": {
                    borderColor: !isSequentialSelected ? colors.orange.base : colors.orange[300],
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.06)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: !isSequentialSelected ? "rgba(251, 142, 0, 0.15)" : "rgba(100, 116, 139, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: !isSequentialSelected ? colors.orange.base : "var(--fx-surface-textsecondary, #64748B)",
                      }}
                    >
                      <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: !isSequentialSelected ? colors.gray[900] : colors.gray[700],
                          lineHeight: 1.2,
                        }}
                      >
                        Timestamped
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: colors.gray[400],
                        }}
                      >
                        Legacy mode
                      </Typography>
                    </Box>
                  </Box>

                  {/* Radio Indicator */}
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: !isSequentialSelected ? `5px solid ${colors.orange.base}` : `2px solid ${colors.gray[300]}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />
                </Box>

                <Typography sx={{ fontSize: 12, color: colors.gray[600], lineHeight: 1.35 }}>
                  Uses unique timestamped IDs for each invoice.
                </Typography>

                <Box
                  sx={{
                    bgcolor: !isSequentialSelected ? "rgba(255, 255, 255, 0.85)" : colors.warmWhite.soft || "rgba(0,0,0,0.02)",
                    border: `1px solid ${!isSequentialSelected ? colors.orange[200] : colors.gray[200]}`,
                    borderRadius: "6px",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.gray[500], textTransform: "uppercase" }}>Example:</Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace, monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      color: !isSequentialSelected ? colors.orange[800] : colors.gray[600],
                    }}
                  >
                    INV-123-20260901
                  </Typography>
                </Box>
              </Box>

              {/* Option 2: Sequential Numbering */}
              <Box
                onClick={() => setMode(INVOICE_NUMBERING_MODES.SEQUENTIAL)}
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  userSelect: "none",
                  border: isSequentialSelected
                    ? `2px solid ${colors.orange.base}`
                    : `1px solid ${colors.gray[200] || "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.1)"}`,
                  bgcolor: isSequentialSelected
                    ? `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`
                    : "var(--fx-surface-base, #FFFFFF)",
                  boxShadow: isSequentialSelected ? "0 4px 14px rgba(251, 142, 0, 0.12)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 1.25,
                  position: "relative",
                  "&:hover": {
                    borderColor: isSequentialSelected ? colors.orange.base : colors.orange[300],
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.06)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: isSequentialSelected ? "rgba(251, 142, 0, 0.15)" : "rgba(100, 116, 139, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isSequentialSelected ? colors.orange.base : "var(--fx-surface-textsecondary, #64748B)",
                      }}
                    >
                      <FormatListNumberedOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: isSequentialSelected ? colors.gray[900] : colors.gray[700],
                          lineHeight: 1.2,
                        }}
                      >
                        Sequential
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: colors.orange[500],
                        }}
                      >
                        Recommended
                      </Typography>
                    </Box>
                  </Box>

                  {/* Radio Indicator */}
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: isSequentialSelected ? `5px solid ${colors.orange.base}` : `2px solid ${colors.gray[300]}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />
                </Box>

                <Typography sx={{ fontSize: 12, color: colors.gray[600], lineHeight: 1.35 }}>
                  Consecutive numbering for job & deposit invoices.
                </Typography>

                <Box
                  sx={{
                    bgcolor: isSequentialSelected ? "rgba(255, 255, 255, 0.85)" : colors.warmWhite.soft || "rgba(0,0,0,0.02)",
                    border: `1px solid ${isSequentialSelected ? colors.orange[200] : colors.gray[200]}`,
                    borderRadius: "6px",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.gray[500], textTransform: "uppercase" }}>Example:</Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace, monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      color: isSequentialSelected ? colors.orange[800] : colors.gray[600],
                    }}
                  >
                    25001, 25002
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Sequential Configuration Panel */}
            <Collapse in={isSequentialSelected} timeout={250} unmountOnExit>
              <Box
                data-swapy-no-drag
                sx={{
                  bgcolor: `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`,
                  border: `1.5px dashed ${colors.orange[300]}`,
                  borderRadius: "14px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  boxShadow: "0 2px 8px rgba(251, 142, 0, 0.04)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.orange.base, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.gray[800], lineHeight: 1.2 }}>
                      {isCurrentlyTimestamped ? "Set starting invoice number" : "Configure next invoice number"}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: colors.gray[600], mt: 0.25, lineHeight: 1.35 }}>
                      {isCurrentlyTimestamped
                        ? "Required to initialize sequential numbering. Invoices already issued will keep their existing IDs."
                        : "You can adjust the next invoice sequence number before the next invoice is created."}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: { sm: "center" } }}>
                  <TextField
                    label={isCurrentlyTimestamped ? "Starting Number" : "Next Invoice Number"}
                    value={value.invoiceStartNumber}
                    onChange={(event) => setStartNumber(event.target.value)}
                    error={!isStartNumberValid}
                    helperText={!isStartNumberValid ? "Please enter a positive whole number (e.g. 25001)." : undefined}
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TagOutlinedIcon sx={{ color: colors.orange.base, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        "&:hover fieldset": {
                          borderColor: colors.orange[300],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.orange.base,
                        },
                      },
                    }}
                  />

                  {isStartNumberValid && value.invoiceStartNumber.trim() && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        px: 1.5,
                        py: 1,
                        borderRadius: "10px",
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        border: `1px solid ${colors.orange[200]}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.gray[500], textTransform: "uppercase" }}>
                        Live Preview
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "monospace, monospace",
                          fontSize: 13,
                          fontWeight: 800,
                          color: colors.orange.base,
                        }}
                      >
                        {value.invoiceStartNumber.trim()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>

            {/* Actions Bar */}
            <Box
              data-swapy-no-drag
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1.5,
                pt: 1,
                mt: "auto",
              }}
            >
              <Button
                sx={{
                  ...outLinedButton,
                  height: "36px",
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontSize: 12.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                disabled={!isDirty || isSaving}
                onClick={reset}
              >
                <RestartAltOutlinedIcon sx={{ fontSize: 16 }} />
                Discard
              </Button>

              <Button
                sx={{
                  ...(isDirty && isStartNumberValid ? brandButton : filledButton),
                  height: "36px",
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: 12.5,
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                disabled={!isDirty || !isStartNumberValid || isSaving}
                onClick={save}
              >
                {isSaving ? (
                  <>
                    <CircularProgress size={15} color="inherit" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckRoundedIcon sx={{ fontSize: 17 }} />
                    Save Changes
                  </>
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </WidgetComponentMaps>
  );
};
